// Phase 0089 — Live shop-floor study API client.
import apiClient from '@/shared/api/client'

export interface StudyMaster {
  id: string
  study_code: string
  study_name: string
  study_category: string
  description: string | null
  default_template_id: string | null
  recommended_frequency: string | null
  priority_rank: number | null
  study_owner_role_code: string | null
  active: boolean
  approval_status: string
}

export interface StudyTemplate {
  id: string
  study_id: string
  template_code: string
  template_name: string
  capture_mode: string
  default_duration_seconds: number | null
  counter_label: string | null
  counter_unit: string | null
  standard_value: number | null
  standard_unit: string | null
  target_comparison_rule: string | null
  evidence_required: boolean
  verification_required: boolean
  capa_required_if_gap: boolean
  scoring_enabled: boolean
  auto_fetch_sources: string[] | null
}

export type SessionStatus =
  | 'DRAFT' | 'IN_PROGRESS' | 'PAUSED' | 'COMPLETED'
  | 'VERIFIED' | 'REJECTED' | 'CANCELLED'

export interface StudySession {
  id: string
  session_no: string
  study_id: string
  template_id: string
  department_id: string | null
  machine_id: string | null
  employee_id: string | null
  shift_id: string | null
  study_date: string
  planned_duration_seconds: number | null
  start_time: string | null
  end_time: string | null
  actual_duration_seconds: number | null
  session_status: SessionStatus
  current_step: string | null
  auto_fetched_context: Record<string, unknown> | null
  result_status: string | null
  gap_found: boolean
  capa_required: boolean
  verification_status: string
  retake_count: number
  retake_reason: string | null
  linked_task_id: string | null
  remarks: string | null
}

export interface StudyResult {
  id: string
  session_id: string
  actual_value: number | null
  standard_value: number | null
  unit: string | null
  gap_value: number | null
  gap_pct: number | null
  status: string | null
  performance_grade: string | null
  training_required: boolean
  method_improvement_required: boolean
  computation_breakdown: Record<string, unknown> | null
  computed_at: string
  // Phase 0093-A
  calculation_confidence?: 'EXACT' | 'ESTIMATED' | 'MANUAL' | 'DATA_NOT_AVAILABLE' | null
}

export interface CAPALink {
  id: string
  session_id: string
  capa_id: string | null
  recommendation_type: string
  recommendation_text: string
  priority: string
  closure_status: string
}

export interface SessionBundle {
  session: StudySession
  template: StudyTemplate | null
  result: StudyResult | null
  tap_count: number
  capa_links: CAPALink[]
}

const P = '/hr/studies'

export const liveStudyApi = {
  listStudies: async (params: { active_only?: boolean; category?: string } = {}) =>
    (await apiClient.get<StudyMaster[]>(P, { params })).data,

  listTemplates: async (study_id?: string) =>
    (await apiClient.get<StudyTemplate[]>(`${P}/templates`,
      { params: study_id ? { study_id } : {} })).data,

  startSession: async (body: {
    template_id: string
    employee_id?: string
    machine_id?: string
    shift_id?: string
    department_id?: string
    supervisor_id?: string
    plant_id?: string
    planned_duration_seconds?: number
    linked_task_id?: string
    remarks?: string
  }) => (await apiClient.post<StudySession>(`${P}/sessions/start`, body)).data,

  getSession: async (id: string) =>
    (await apiClient.get<SessionBundle>(`${P}/sessions/${id}`)).data,
  // Phase 0093-A — session recovery payload (includes timer_events,
  // observations, last_autosave_at, server-computed elapsed_ms).
  recoverSession: async (id: string) =>
    (await apiClient.get<{
      session: StudySession
      template: StudyTemplate | null
      tap_count: number
      elapsed_ms: number | null
      timer_events: Array<{ event_type: string; event_time: string; elapsed_ms: number | null }>
      observations: Array<Record<string, unknown>>
      last_autosave_at: string | null
      resumable: boolean
    }>(`${P}/sessions/${id}/recover`)).data,

  startTimer: async (id: string) =>
    (await apiClient.post<StudySession>(`${P}/sessions/${id}/timer/start`)).data,
  stopTimer: async (id: string) =>
    (await apiClient.post<StudySession>(`${P}/sessions/${id}/timer/stop`)).data,
  tap: async (id: string) =>
    (await apiClient.post<{ tap_no: number; tap_count: number; elapsed_ms: number; rejected_after_auto_stop?: boolean }>(
      `${P}/sessions/${id}/counter/tap`)).data,
  undoTap: async (id: string) =>
    (await apiClient.post<{ undone_tap_no: number | null; tap_count: number }>(
      `${P}/sessions/${id}/counter/undo`)).data,
  observation: async (id: string, body: {
    field_code?: string
    observation_text?: string
    observation_numeric?: number
    observation_bool?: boolean
    activity_type?: string
  }) => (await apiClient.post(`${P}/sessions/${id}/observation`, body)).data,

  complete: async (id: string) =>
    (await apiClient.post<StudySession>(`${P}/sessions/${id}/complete`)).data,
  verify: async (id: string, body: { approved: boolean; remarks?: string }) =>
    (await apiClient.post<StudySession>(`${P}/sessions/${id}/verify`, body)).data,
  retake: async (id: string, reason: string) =>
    (await apiClient.post<StudySession>(`${P}/sessions/${id}/retake`, { reason })).data,

  history: async (status?: string, limit = 50) =>
    (await apiClient.get<StudySession[]>(`${P}/history`,
      { params: { status, limit } })).data,
  dashboard: async () =>
    (await apiClient.get(`${P}/dashboard`)).data,
  closeCAPA: async (id: string, remarks?: string) =>
    (await apiClient.post(`${P}/capa-links/${id}/close`, { remarks })).data,

  // ── Phase 0093-B — AUTO_FETCH_SUMMARY capture mode ──
  autoFetchSummary: async (id: string) =>
    (await apiClient.get<AutoFetchSummary>(
      `${P}/sessions/${id}/auto-fetch-summary`)).data,
  submitAutoFetchSummary: async (
    id: string,
    body: {
      chip_selections?: Record<string, string>
      manual_fields?: Record<string, string>
      remarks?: string
    },
  ) => (await apiClient.post(
    `${P}/sessions/${id}/auto-fetch-summary/submit`, body)).data,
}

export interface AutoFetchSummaryCard {
  label: string
  value: number | string | null
  unit: string | null
  status: 'OK' | 'INFO' | 'WARN' | 'ALERT'
}

export interface AutoFetchSummaryChipGroup {
  code: string
  label: string
  single_select: boolean
  options: string[]
}

export interface AutoFetchSummary {
  study_code: string
  summary_cards: AutoFetchSummaryCard[]
  primary_metric: {
    label: string
    value: number | string | null
    unit: string | null
    target?: number | null
    status: 'ON_TARGET' | 'GAP_FOUND' | 'DATA_NOT_AVAILABLE'
  }
  source_status: Record<string, 'OK' | 'MISSING'>
  missing_sources: string[]
  calculation_confidence: 'EXACT' | 'ESTIMATED' | 'MANUAL' | 'DATA_NOT_AVAILABLE'
  recommended_result_status: 'ON_TARGET' | 'GAP_FOUND' | 'DATA_NOT_AVAILABLE'
  manual_fields_required: string[]
  chip_groups: AutoFetchSummaryChipGroup[]
  capa_recommendation: {
    type: string
    owner_role: string
    text: string
  } | null
}
